import { ApiResult, EnsApi, EnsName } from '../../application/ens/ports';
import { fullName } from '../../domain/ens/ens';

/** The pepes.dog ENS API under /api/ens, plus the ETH price from CoinGecko. */

type RawSubname = {
  label?: string;
  name?: string;
  fullName?: string;
  avatar?: string;
  addresses?: Record<string, string>;
  texts?: Record<string, string> | Array<{ key: string; value: string }>;
};

const textOf = (s: RawSubname, key: string): string | undefined => {
  if (!s.texts) return undefined;
  if (Array.isArray(s.texts)) return s.texts.find((t) => t.key === key)?.value;
  return s.texts[key];
};

export const toEnsName = (s: RawSubname): EnsName => {
  const label = s.label || (s.name || s.fullName || '').split('.')[0];
  return { label, name: s.name || s.fullName || fullName(label), address: s.addresses?.['60'], avatar: s.avatar || textOf(s, 'avatar') };
};

async function post(url: string, body: unknown): Promise<ApiResult> {
  const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  let data: { success?: boolean; error?: string; details?: string } = {};
  try {
    data = await r.json();
  } catch {
    /* an empty or broken body still has a status */
  }
  return { ok: r.ok && data.success !== false, status: r.status, error: data.error, details: data.details };
}

export const httpEnsApi: EnsApi = {
  async isAvailable(label) {
    const r = await fetch(`/api/ens/check?subname=${encodeURIComponent(label)}`);
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || 'Could not check the name');
    return !!data.available;
  },
  mint: (req) => post('/api/ens/mint', { subname: req.label, address: req.address, txHash: req.txHash, issuedAt: req.issuedAt, signature: req.signature }),
  async recent() {
    const r = await fetch('/api/ens/recent');
    const data = await r.json();
    const s = data?.subnames || {};
    const items: RawSubname[] = s.items || s.data || (Array.isArray(s) ? s : []);
    return { names: items.map(toEnsName), total: s.total || items.length };
  },
  async mine(address) {
    const r = await fetch(`/api/ens/user-subnames?address=${address}&_t=${Date.now()}`, { cache: 'no-cache' });
    const data = await r.json();
    return data.success && Array.isArray(data.subnames) ? data.subnames.map(toEnsName) : [];
  },
  async ethUsd() {
    const r = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd');
    const data = await r.json();
    return Number(data?.ethereum?.usd) || 0;
  },
  async uploadAvatar(file, address) {
    const form = new FormData();
    form.append('avatar', file);
    form.append('address', address);
    const r = await fetch('/api/ens/upload-avatar', { method: 'POST', body: form });
    const data = await r.json();
    if (!data.success || !data.avatarUrl) throw new Error(data.error || 'Upload failed');
    return data.avatarUrl;
  },
  updateAddress: (req) =>
    post('/api/ens/update-address', { subname: req.label, newAddress: req.newAddress, currentAddress: req.currentAddress, issuedAt: req.issuedAt, signature: req.signature }),
  updateAvatar: (req) =>
    post('/api/ens/update-avatar', { subname: req.label, currentAddress: req.currentAddress, avatarUrl: req.avatarUrl, issuedAt: req.issuedAt, signature: req.signature }),
};
